<template>
  <div class="service-details-container relative min-h-screen py-20">
    <!-- Background -->
    <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 opacity-40"></div>
    
    <!-- Header -->
    <div class="relative z-10 text-center mb-16">
      <h2 class="text-4xl md:text-6xl font-bold text-white mb-6">
        Complete <span class="text-primary">Service</span> Solutions
      </h2>
      <p class="text-xl text-white/80 max-w-3xl mx-auto mb-8">
        From DOT inspections to major accident repairs, we provide comprehensive services to keep your equipment running.
      </p>
    </div>

    <!-- Services Grid -->
    <div class="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto px-6">
      <!-- ASME-R Certified -->
      <div class="service-item" ref="service1">
        <div class="service-image">
          <img src="/img/service/asme-certified.jpg" alt="ASME-R Certified" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'certificate']" />
          </div>
        </div>
        <div class="service-content">
          <h3>ASME-R Certified</h3>
          <p>Enables us to service pressure vessels, guaranteeing that repairs adhere to or surpass original construction standards, assisting companies in meeting legal and safety requirements.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- Accident Repairs -->
      <div class="service-item" ref="service2">
        <div class="service-image">
          <img src="/img/service/accident-repairs.jpg" alt="Accident Repairs" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'hammer']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Accident Repairs</h3>
          <p>Whether it's a minor accident that caused dents in the exterior wrap or a rollover needing significant repairs, our team of experts can restore your trailer to a functional asset.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>
      <!-- Running Gear Replacement -->
      <div class="service-item" ref="service3">
        <div class="service-image">
          <img src="/img/service/running-gear.jpg" alt="Running Gear Replacement" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'cog']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Running Gear Replacement</h3>
          <p>Whether you need a complete suspension replacement or just a repair on axle or hub components, we have the expertise and equipment to get you back on the road swiftly.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- Electrical • Lighting Repairs -->
      <div class="service-item" ref="service4">
        <div class="service-image">
          <img src="/img/service/electrical-lighting.jpg" alt="Electrical and Lighting Repairs" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'lightbulb']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Electrical • Lighting Repairs</h3>
          <p>We carry a wide range of standard lighting components and can either replace single lights or install a completely new lighting system, depending on what is needed.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- Hydraulic Work -->
      <div class="service-item" ref="service5">
        <div class="service-image">
          <img src="/img/service/hydraulic-work.jpg" alt="Hydraulic Work" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'wrench']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Hydraulic Work</h3>
          <p>If you have a simple leak or need an entire overhaul, our team can design and fabricate the best solution and assemble from high-quality components.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>
      <!-- Pump Platform Fabrication -->
      <div class="service-item" ref="service6">
        <div class="service-image">
          <img src="/img/service/pump-platform.jpg" alt="Pump Platform Fabrication" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'industry']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Pump Platform Fabrication</h3>
          <p>Should you require a trailer-mounted pump, we are capable of designing, fabricating, and installing the pump platform to ensure maximum efficiency, no matter your setup needs.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- Inner Barrel Repairs -->
      <div class="service-item" ref="service7">
        <div class="service-image">
          <img src="/img/service/inner-barrel.jpg" alt="Inner Barrel Repairs" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'tools']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Inner Barrel Repairs</h3>
          <p>We work on any brand of stainless steel tanks. If you have a leak and need an expert to diagnose the problem and recommend the right repair, bring your trailer in.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- DOT Inspections -->
      <div class="service-item" ref="service8">
        <div class="service-image">
          <img src="/img/service/dot-inspections.jpg" alt="DOT Inspections" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'clipboard-check']" />
          </div>
        </div>
        <div class="service-content">
          <h3>DOT Inspections</h3>
          <p>Ensure that your trailer is safe and compliant by allowing us to do your DOT inspections. Our team has the ability to inspect and perform any work to get you back on the road quickly.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>
      <!-- Safety Handrails Fabrication -->
      <div class="service-item" ref="service9">
        <div class="service-image">
          <img src="/img/service/safety-handrails.jpg" alt="Safety Handrails Fabrication" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'shield-alt']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Safety Handrails Fabrication</h3>
          <p>The safety of your drivers is paramount and ensuring that they have the proper handrails to safely access the top of the tank is critical. We offer many design options to suit your specific needs.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- Walkway Fabrication -->
      <div class="service-item" ref="service10">
        <div class="service-image">
          <img src="/img/service/walkway-fabrication.jpg" alt="Walkway Fabrication" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'road']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Walkway Fabrication</h3>
          <p>If your trailer's walkway is not sufficient to allow safe access or has come into disrepair, let us fabricate and install a new walkway to ensure the job is done completely.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- Ladder Repair • Replacement -->
      <div class="service-item" ref="service11">
        <div class="service-image">
          <img src="/img/service/ladder-repair.jpg" alt="Ladder Repair and Replacement" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'stairs']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Ladder Repair • Replacement</h3>
          <p>Ladders often get damaged when backing up and our available ladder designs are able to be installed quickly and carefully to maintain the access your drivers require.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>
      <!-- Fifth Wheel Plate Replacement -->
      <div class="service-item" ref="service12">
        <div class="service-image">
          <img src="/img/service/fifth-wheel-plate.jpg" alt="Fifth Wheel Plate Replacement" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'record-vinyl']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Fifth Wheel Plate Replacement</h3>
          <p>We only use heavy duty plates and can replace any compromised or damaged plates to ensure that your trailer is being operated under the safest conditions.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- Fender • Fender Bracket Repair -->
      <div class="service-item" ref="service13">
        <div class="service-image">
          <img src="/img/service/fender-repair.jpg" alt="Fender and Fender Bracket Repair" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'car-side']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Fender • Fender Bracket Repair</h3>
          <p>We stock most common fender makes and models and can quickly remove and replace your fenders, replace any damaged brackets and return your trailer to service.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- Custom Cabinet Fabrication -->
      <div class="service-item" ref="service14">
        <div class="service-image">
          <img src="/img/service/custom-cabinet.jpg" alt="Custom Cabinet Fabrication" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'box']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Custom Cabinet Fabrication</h3>
          <p>If you trailer needs a different cabinet for the application or simply a replacement due to damage, we can customize, fabricate and install one to meet your needs.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>

      <!-- Landing Leg Replacement -->
      <div class="service-item" ref="service15">
        <div class="service-image">
          <img src="/img/service/landing-leg.jpg" alt="Landing Leg Replacement" />
          <div class="service-overlay">
            <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
          </div>
        </div>
        <div class="service-content">
          <h3>Landing Leg Replacement</h3>
          <p>If you need new landing legs due to an accident or simply misuse or abuse, we offer a variety of options to replace and reinforce your landing legs for optimal use.</p>
          <button class="learn-more-btn">Learn more</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'ServiceDetailsSection',
  mounted() {
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    if (this.observers) {
      this.observers.forEach(observer => observer.disconnect());
    }
  },
  data() {
    return {
      observers: []
    };
  },
  methods: {
    setupIntersectionObserver() {
      for (let i = 1; i <= 15; i++) {
        const serviceRef = this.$refs[`service${i}`];
        if (serviceRef) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                setTimeout(() => {
                  entry.target.classList.add('animate-in');
                }, (i - 1) * 100);
              }
            });
          }, { threshold: 0.1 });
          
          observer.observe(serviceRef);
          this.observers.push(observer);
        }
      }
    }
  }
};
</script>
<style scoped>
.service-item {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  opacity: 0;
  transform: translateY(30px);
  backdrop-filter: blur(8px);
}

.service-item.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.service-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  border-color: rgba(162, 42, 33, 0.4);
}

.service-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.service-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
  filter: brightness(0.7);
}

.service-item:hover .service-image img {
  transform: scale(1.1);
  filter: brightness(0.9);
}

.service-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(162, 42, 33, 0.8), rgba(0, 0, 0, 0.6));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  font-size: 2.5rem;
  color: white;
}

.service-item:hover .service-overlay {
  opacity: 1;
}

.service-content {
  padding: 1.5rem;
  color: white;
}

.service-content h3 {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  color: white;
}

.service-content p {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.learn-more-btn {
  background: #a22a21;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.learn-more-btn:hover {
  background: #c23529;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .service-item {
    margin-bottom: 1rem;
  }
}
</style>